// Sheep Tag's unlock, bus routing, gain fades, visibility suspension and take
// exclusion, adapted to Footmen's lazy banks. No audio randomness enters the sim.
import { CROSSFADE, chooseTrack, nextMusicPhase, shouldWarm, takeIndex, voiceAllowed, type Manifest, type Phase } from './audio-logic';
import type { Settings } from './ui/settings';
type Voice = { source: AudioBufferSourceNode; gain: GainNode; kind: string; end: number };
type Loop = { key: string; voice?: Voice; pending?: string; serial: number; fading?: Set<Voice> };
type Stream = { element: HTMLAudioElement; gain: GainNode; file: string; serial: number; stopAt: number };
export class GameAudio {
  private ctx?: AudioContext;
  private master?: GainNode;
  private buses: Partial<Record<'music' | 'sfx' | 'ambience', GainNode>> = {};
  private manifest?: Manifest;
  private manifestPending = false;
  private failedUntil = new Map<string, number>();
  private buffers = new Map<string, Promise<AudioBuffer>>();
  private last = new Map<string, number>();
  private takes = new Map<string, number>();
  private voices = new Set<Voice>();
  private pending = new Map<string, number>();
  private streams: Stream[] = [];
  private active?: Stream;
  private beds = new Map<string, Loop>();
  private epoch = 0;
  private paused = false;
  private settings: Pick<Settings, 'sound' | 'music'> = { sound: 'on', music: 'on' };
  private loaded = new Set<string>();
  private duckUntil = 0;
  constructor() {
    const unlock = () => this.unlock();
    addEventListener('pointerdown', unlock, { capture: true });
    addEventListener('keydown', unlock, { capture: true });
    document.addEventListener('visibilitychange', () => this.suspend());
    document.addEventListener('click', e => {
      if ((e.target as Element).closest?.('button:not(:disabled), [role="button"]')) this.play('shared/click', 0.35);
    }, true);
    document.addEventListener('pointerover', e => {
      const b = (e.target as Element).closest?.('button:not(:disabled)');
      if (b && !b.contains(e.relatedTarget as Node | null)) this.play('shared/hover', 0.12);
    });
  }
  private unlock() {
    if (!this.ctx) {
      const C = globalThis.AudioContext ?? (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!C) return;
      try { this.ctx = new C(); } catch { return; }
      const c = this.ctx;
      this.master = c.createGain();
      const comp = c.createDynamicsCompressor(); comp.threshold.value = -12; comp.ratio.value = 4;
      this.master.connect(comp).connect(c.destination);
      for (const k of ['music', 'sfx', 'ambience'] as const) { const bus = c.createGain(); bus.connect(this.master); this.buses[k] = bus; }
      // Start a silent buffer synchronously inside the first gesture, including iOS.
      const silent = c.createBufferSource(); silent.buffer = c.createBuffer(1, 1, c.sampleRate); silent.connect(c.destination); silent.start();
      // Both media elements and their source nodes are created in the unlock gesture.
      // Prime each element with a tiny silent WAV so later asynchronous play is allowed on iOS.
      for (let i = 0; i < 2; i++) {
        const element = new Audio(); element.preload = 'none'; element.loop = true;
        const gain = c.createGain(); gain.gain.value = 0;
        c.createMediaElementSource(element).connect(gain).connect(this.buses.music!);
        const stream: Stream = { element, gain, file: '', serial: 0, stopAt: 0 };
        this.streams.push(stream);
        element.src = 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQIAAAAAAA==';
        const serial = stream.serial;
        void element.play().then(() => { if (serial === stream.serial) element.pause(); }).catch(() => {});
      }
      this.apply(this.settings);
    }
    if (!this.manifest && !this.manifestPending) {
      this.manifestPending = true;
      void fetch('audio/manifest.json').then(r => { if (!r.ok) throw new Error('audio manifest'); return r.json(); }).then((m: Manifest) => { this.manifest = m; this.warm('shared'); }).catch(() => {}).finally(() => { this.manifestPending = false; });
    }
    this.suspend();
  }
  private suspend() {
    if (!this.ctx) return;
    if (document.hidden) { this.stopMusic(); void this.ctx.suspend().catch(() => {}); }
    else if (this.ctx.state !== 'running') void this.ctx.resume().catch(() => {});
  }
  pause(value: boolean) {
    if (value === this.paused) return;
    this.paused = value;
    if (value) { this.stopLoops(); for (const v of [...this.voices]) if (!this.isUI(v.kind)) this.stop(v); }
    this.suspend();
  }
  private isUI(kind: string) { return kind === 'shared/click' || kind === 'shared/hover'; }
  apply(s: Pick<Settings, 'sound' | 'music'>) {
    const changed = this.settings.sound !== s.sound || this.settings.music !== s.music;
    this.settings = { sound: s.sound, music: s.music };
    if (!this.ctx || !this.master) return;
    this.master.gain.value = s.sound === 'on' ? 0.75 : 0;
    this.buses.music!.gain.value = s.music === 'on' ? 0.55 : 0;
    this.buses.sfx!.gain.value = 0.7; this.buses.ambience!.gain.value = 0.4;
    if (changed && s.sound === 'off') { this.epoch++; this.loaded.clear(); this.stopLoops(); for (const v of [...this.voices]) this.stop(v); }
    if (s.music === 'off') this.stopMusic();
  }
  reset() {
    this.epoch++; this.stopLoops();
    for (const v of [...this.voices]) if (!this.isUI(v.kind)) this.stop(v);
    this.last.clear();
    for (const file of this.buffers.keys()) if (!file.startsWith('shared/') && !file.startsWith('ambience/')) this.buffers.delete(file);
    this.loaded.clear();
  }
  private stop(v: Voice) { try { v.source.stop(); } catch { /* already ended */ } this.voices.delete(v); }
  private stopLoops() {
    this.stopMusic();
    for (const l of this.beds.values()) this.stopLoop(l);
  }
  private stopLoop(l: Loop) {
    l.serial++; l.pending = undefined;
    if (l.voice) this.stop(l.voice);
    for (const v of l.fading ?? []) this.stop(v);
    l.fading?.clear(); l.voice = undefined; l.key = '';
  }
  private buffer(file: string) {
    if ((this.failedUntil.get(file) ?? 0) > performance.now()) return Promise.reject(new Error('audio backoff'));
    let p = this.buffers.get(file);
    if (!p) {
      p = fetch(`audio/${file}`).then(r => { if (!r.ok) throw new Error(file); return r.arrayBuffer(); }).then(b => this.ctx!.decodeAudioData(b));
      this.buffers.set(file, p); p.catch(() => { this.buffers.delete(file); this.failedUntil.set(file, performance.now() + 5000); });
    }
    return p;
  }
  warm(folder: string) {
    if (!this.ctx || !this.manifest || !shouldWarm(this.settings.sound, this.loaded.has(folder))) return;
    this.loaded.add(folder);
    const epoch = this.epoch;
    // One at a time: shared/UI first, then only armies present in this match.
    void (async () => { for (const [kind, files] of Object.entries(this.manifest!.sfx)) if (kind.startsWith(folder+'/')) for (const f of files) { if (epoch !== this.epoch) return; try { await this.buffer(f); } catch { /* a later play retries */ } } })();
  }
  private source(buffer: AudioBuffer, kind: string, bus: 'sfx' | 'ambience', level: number, rate = 1, delay = 0): Voice {
    const c = this.ctx!, source = c.createBufferSource(), gain = c.createGain();
    source.buffer = buffer; source.playbackRate.value = rate; gain.gain.value = level;
    source.connect(gain).connect(this.buses[bus]!);
    const v = { source, gain, kind, end: c.currentTime + delay + buffer.duration/rate };
    if (bus === 'sfx') this.voices.add(v);
    source.onended = () => { this.voices.delete(v); source.disconnect(); gain.disconnect(); };
    source.start(c.currentTime + delay); return v;
  }
  play(kind: string, level = 1, important = false, delay = 0, after?: string, cueKey = kind): void {
    const c = this.ctx, files = this.manifest?.sfx[kind];
    if (!c || c.state !== 'running' || this.settings.sound === 'off' || !files?.length || (this.paused && !this.isUI(kind)) || level < 0.005) return;
    const copies = [...this.voices].filter(v => v.kind === kind).length + (this.pending.get(kind) ?? 0);
    const total = this.voices.size + [...this.pending.values()].reduce((a,b) => a+b, 0);
    if (!important && total >= 20) return; // leave four slots for warnings and hero/army moments
    const cooldownKey = `${cueKey}/${important}`;
    if (!voiceAllowed(important ? 0 : copies, total, c.currentTime, this.last.get(cooldownKey) ?? -Infinity, important ? 0.08 : 0.16)) return;
    const n = takeIndex(files.length, this.takes.get(kind) ?? -1, Math.random()); this.takes.set(kind,n);
    this.last.set(cooldownKey,c.currentTime); this.pending.set(kind, (this.pending.get(kind) ?? 0)+1);
    const epoch = this.epoch, requested = c.currentTime;
    void this.buffer(files[n]).then(b => {
      if ((!this.isUI(kind) && (epoch !== this.epoch || this.paused)) || c.state !== 'running' || this.settings.sound === 'off' || (!important && c.currentTime-requested > 0.3)) return;
      this.source(b,kind,'sfx',level*(0.9+Math.random()*0.2),0.97+Math.random()*0.06,delay);
      if (important && level >= 0.2 && !this.isUI(kind)) this.duckUntil = c.currentTime+delay+2;
      if (after) this.play(after,level,true,delay+b.duration/0.97);
    }).catch(() => {}).finally(() => this.pending.set(kind, Math.max(0,(this.pending.get(kind) ?? 1)-1)));
  }
  pick(army: string) {
    // A call belongs only to a real selection, never a watch/unmute transition. The theme no longer waits 0.8 s for it
    // (the pick has usually put it on already, and a wait kept the last card's theme on: Flash F5); the call ducks it.
    this.play(`${army}/call`,1,true);
  }
  private clearStream(s: Stream) {
    s.serial++; s.element.pause(); s.element.removeAttribute('src'); s.element.load();
    s.file = ''; s.stopAt = 0;
    s.gain.gain.cancelScheduledValues(this.ctx!.currentTime); s.gain.gain.value = 0;
  }
  /** The two music elements as they are (window.__qa.music under ?qa=1): which file each holds, whether it plays, its
   *  fade gain and position, and which one is the theme now. */
  musicState() {
    return { on: this.settings.sound === 'on' && this.settings.music === 'on', active: this.active?.file ?? null,
      streams: this.streams.map(s => ({ file: s.file, playing: !s.element.paused, gain: +s.gain.gain.value.toFixed(2), at: +s.element.currentTime.toFixed(2), active: s === this.active })) };
  }
  private stopMusic() {
    for (const s of this.streams) this.clearStream(s);
    this.active = undefined;
  }
  private prepare(s: Stream, file: string) {
    if (s.file === file) return;
    this.clearStream(s); s.file = file;
    s.element.preload = 'auto'; s.element.src = `audio/${file}`; s.element.load();
  }
  /** Ramp one element's fade from where it is now to `to`, at one track's worth per CROSSFADE (a half-risen theme
   *  goes down in half the time; cancelScheduledValues alone would drop a ramp in flight back to its start). */
  private ramp(s: Stream, to: number) {
    const g = s.gain.gain, now = this.ctx!.currentTime, from = g.value, end = now + Math.abs(to - from) * CROSSFADE;
    g.cancelScheduledValues(now); g.setValueAtTime(from, now); g.linearRampToValueAtTime(to, end);
    return end;
  }
  /** `incoming` rises as the theme now falls, at the same rate, so the two never add up past one track at full. */
  private crossfade(incoming: Stream) {
    if (this.active && this.active !== incoming) this.active.stopAt = this.ramp(this.active, 0) + 0.05;
    incoming.stopAt = 0; this.ramp(incoming, 1); this.active = incoming;
  }
  private streamMusic(file: string) {
    const now = this.ctx!.currentTime;
    if (this.active?.file === file) return;
    if ((this.failedUntil.get(file) ?? 0) > performance.now()) return;
    const others = this.streams.filter(s => s !== this.active), fading = (s: Stream) => s.stopAt > now && s.stopAt !== Infinity;
    // back to a theme still fading out: it turns straight back up, no restart and no play() to wait on (Flash F2)
    const back = others.find(s => s.file === file && fading(s) && !s.element.paused);
    if (back) { this.crossfade(back); return; }
    const incoming = others.find(s => !fading(s));
    if (!incoming) {
      // both elements still heard (taps through a pick): the theme just tapped past goes back down from where it is,
      // so its element frees first and it never swells to full (Flash F1); update() asks again each frame
      if (this.active) { this.active.stopAt = this.ramp(this.active, 0) + 0.05; this.active = undefined; }
      return;
    }
    this.prepare(incoming, file);
    if (incoming.stopAt === Infinity) return; // play promise already pending
    incoming.stopAt = Infinity;
    const serial = ++incoming.serial;
    void incoming.element.play().then(() => {
      if (serial === incoming.serial) this.crossfade(incoming);
    }).catch(() => {
      if (serial !== incoming.serial) return;
      this.clearStream(incoming); this.failedUntil.set(file, performance.now() + 5000);
    });
  }
  update(army: string | null, phase: Phase, ambience: Record<string, number>, seconds = 0) {
    const c = this.ctx;
    if (!c || this.paused || !this.manifest || c.state !== 'running' || this.settings.sound === 'off') return;
    const gain = this.buses.music!.gain;
    gain.setTargetAtTime(this.settings.music === 'off' ? 0 : c.currentTime < this.duckUntil ? 0.32 : 0.55,c.currentTime,0.2);
    if (this.settings.music === 'on') {
      for (const s of this.streams) if (s.stopAt && s.stopAt <= c.currentTime) this.clearStream(s);
      const track = chooseTrack(this.manifest.music, army, phase);
      // Invalidate a pending transition when the desired army/phase changes.
      for (const s of this.streams) if (s !== this.active && s.stopAt === Infinity && s.file !== track?.file) this.clearStream(s);
      if (track) this.streamMusic(track.file);
      const nextPhase = nextMusicPhase(phase, seconds);
      const next = nextPhase && army ? chooseTrack(this.manifest.music, army, nextPhase) : undefined;
      const spare = this.streams.find(s => s !== this.active && !s.stopAt);
      if (spare && this.active?.file === track?.file) {
        if (next) this.prepare(spare, next.file);
        else if (spare.file) this.clearStream(spare);
      }
    }
    this.warm('shared');
    for (const [kind, level] of Object.entries(ambience)) {
      let l = this.beds.get(kind); if (!l) { l = {key:'',serial:0}; this.beds.set(kind,l); }
      if (l.voice) l.voice.gain.gain.setTargetAtTime(level,c.currentTime,1);
      if (level < 0.01) { if (l.voice && c.currentTime >= l.voice.end) l.voice = undefined; continue; }
      if (!l.pending && (!l.voice || c.currentTime >= l.voice.end-1.5)) {
        const name = `ambience/${kind}`, files = this.manifest.sfx[name];
        if (!files) continue;
        const n = takeIndex(files.length,this.takes.get(name) ?? -1,Math.random()); this.takes.set(name,n);
        this.loop(l,kind,files[n],name,'ambience',level);
      }
    }
  }
  private loop(l: Loop, key: string, file: string, kind: string, bus: 'ambience', level: number, delay = 0) {
    const serial = ++l.serial, epoch = this.epoch; l.pending = key;
    void this.buffer(file).then(b => {
      if (serial !== l.serial || epoch !== this.epoch || this.ctx!.state !== 'running') return;
      const now = this.ctx!.currentTime;
      if (l.voice) {
        const old = l.voice; (l.fading ??= new Set()).add(old);
        const ended = old.source.onended;
        old.source.onended = e => { ended?.call(old.source, e); l.fading?.delete(old); };
        old.gain.gain.cancelScheduledValues(now); old.gain.gain.setTargetAtTime(0,now,0.4); old.source.stop(now+2);
      }
      l.voice = this.source(b,kind,bus,0,1,delay); l.voice.gain.gain.setValueAtTime(0,now+delay); l.voice.gain.gain.linearRampToValueAtTime(level,now+delay+1.5); l.key = key;
    }).catch(() => {}).finally(() => { if (serial === l.serial) l.pending = undefined; });
  }
}
